/* Settings: the trainer's setup, the viewer's switches. The pages read and write them on the
   device, in IndexedDB, which is what keeps the app working offline; the ones worth having
   everywhere also travel to `profiles.settings`, under the same keys, around that store. */

import { browserStore, type KV } from "@preflop/api";

/** The settings that follow the account. The rest are about this device: whether edit mode
    was left on here, and this device's memory of the active collection. */
export const SYNCED_SETTINGS = [
  "practiceConfig",
  "randomizer",
  "catalogAdds",
  "saveTarget",
  "seatNames",
  "dailyGoal",
];

/** Set while this device holds a synced setting the profile has not been told of. */
const DIRTY = "settingsDirty";
const PUSH_DELAY_MS = 500;

export type PushSettings = (settings: Record<string, unknown>) => Promise<void>;

export interface Settings {
  load<T>(key: string, fallback: T): Promise<T>;
  save(key: string, value: unknown): Promise<void>;
  /** Bring the device and the profile into line, once, when a page opens. The device wins
      when it holds changes it never managed to push; otherwise the profile does. Keys the
      profile lacks are adopted from the device. Resolves before any page reads a setting, and
      from then on a save of a synced key is followed by a push. */
  reconcile(remote: Record<string, unknown>, push: PushSettings): Promise<void>;
  /** Drop what belongs to an account: its synced settings, a pending push, and the mark that
      one is owed. On sign-out, and when another user turns up on this device — otherwise the
      next account to open a page here would adopt the last one's settings as its own. */
  forget(): Promise<void>;
  /** For tests: the pending push, run now. */
  flush(): Promise<void>;
}

export function createSettings(store: KV, delay = PUSH_DELAY_MS): Settings {
  let pushTo: PushSettings | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const synced = async (): Promise<Record<string, unknown>> => {
    const out: Record<string, unknown> = {};
    for (const key of SYNCED_SETTINGS) {
      const value = await store.get<unknown>(key);
      if (value !== undefined) out[key] = value;
    }
    return out;
  };

  /** Everything synced, as the device has it now. Dirty until the profile has taken it. */
  async function push(): Promise<void> {
    if (!pushTo) return;
    try {
      await pushTo(await synced());
      await store.delete(DIRTY);
    } catch {
      // Offline, most likely. The mark stays, and the next page to open pushes.
    }
  }

  return {
    load: async <T>(key: string, fallback: T) => (await store.get<T>(key)) ?? fallback,

    async save(key, value) {
      await store.set(key, value);
      if (!SYNCED_SETTINGS.includes(key)) return;
      await store.set(DIRTY, true);
      // One push for a burst of clicks, carrying all of them.
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void push();
      }, delay);
    },

    async reconcile(remote, to) {
      pushTo = to;
      if (await store.get<boolean>(DIRTY)) return push();
      let adopt = false;
      for (const key of SYNCED_SETTINGS) {
        if (key in remote) await store.set(key, remote[key]);
        else if ((await store.get<unknown>(key)) !== undefined) adopt = true;
      }
      if (adopt) await push();
    },

    async forget() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      pushTo = null;
      for (const key of [...SYNCED_SETTINGS, DIRTY]) await store.delete(key);
    },

    async flush() {
      if (timer === null) return;
      clearTimeout(timer);
      timer = null;
      await push();
    },
  };
}

const settings = createSettings(browserStore("settings"));

// A setting changed just before the tab is closed or the phone is put away should not wait
// for the next visit to reach the other devices: a page going to the background pushes now.
if (typeof document !== "undefined")
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void settings.flush();
  });

export const loadSetting = settings.load;
export const saveSetting = settings.save;
export const reconcileSettings = settings.reconcile;
export const forgetAccountSettings = settings.forget;
